import React, { useState, useMemo, useEffect } from "react";
import {
  ShoppingCart,
  Database,
  TrendingUp,
  Percent,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const formatDateSimple = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export default function ProfitSalesReportTab({
  reportData,
  loading,
  searchTerm = "",
}) {
  const [expandedTxIds, setExpandedTxIds] = useState({});
  const [showAllItemsMap, setShowAllItemsMap] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const transactions = useMemo(() => {
    return reportData?.transactions || [];
  }, [reportData]);

  const toggleExpand = (id) => {
    setExpandedTxIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleShowAllItems = (id) => {
    setShowAllItemsMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Filter transactions by search term (no invoice, customer, or product name)
  const filteredTransactions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return transactions;

    return transactions.filter((tx) => {
      const matchNo = (tx.transaction_no || "").toLowerCase().includes(term);
      const matchCust = (tx.customer || "").toLowerCase().includes(term);
      const matchProd = (tx.items || []).some((itm) =>
        (itm.product_name || "").toLowerCase().includes(term)
      );
      return matchNo || matchCust || matchProd;
    });
  }, [transactions, searchTerm]);

  // Reset page when search or data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, reportData]);

  const totalFilteredCount = filteredTransactions.length;
  const maxPage = Math.ceil(totalFilteredCount / pageSize) || 1;
  const safeCurrentPage = Math.min(Math.max(1, currentPage), maxPage);
  
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalFilteredCount);
  const paginatedTransactions = filteredTransactions.slice(startIdx, endIdx);

  // Overall calculations
  const totalSales = Number(reportData?.total_sales || 0);
  const totalModal = Number(reportData?.total_modal || 0);
  const totalProfit = Number(reportData?.total_profit || 0);
  const profitMargin = Number(reportData?.profit_margin_percent || 0).toLocaleString("id-ID", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* === 4 SUMMARY CARDS === */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Penjualan */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center text-sky-600 flex-shrink-0">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              TOTAL PENJUALAN
            </p>
            <p className="text-lg sm:text-xl font-extrabold text-slate-900 truncate">
              Rp {fmt(totalSales)}
            </p>
          </div>
        </div>

        {/* Card 2: Total Modal */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600 flex-shrink-0">
            <Database className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              TOTAL MODAL
            </p>
            <p className="text-lg sm:text-xl font-extrabold text-slate-900 truncate">
              Rp {fmt(totalModal)}
            </p>
          </div>
        </div>

        {/* Card 3: Total Profit */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-[#0f766e] flex-shrink-0">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              TOTAL PROFIT
            </p>
            <p className="text-lg sm:text-xl font-extrabold text-[#0f766e] truncate">
              Rp {fmt(totalProfit)}
            </p>
          </div>
        </div>

        {/* Card 4: Margin Profit */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-600 flex-shrink-0">
            <Percent className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              MARGIN PROFIT
            </p>
            <p className="text-lg sm:text-xl font-extrabold text-slate-900 truncate">
              {profitMargin}%
            </p>
          </div>
        </div>
      </div>

      {/* === RINCIAN PER TRANSAKSI === */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-800 tracking-tight">
            Rincian per Transaksi
          </h2>
          {totalFilteredCount > 0 && (
            <span className="text-xs text-slate-500">
              Menampilkan {totalFilteredCount === 0 ? 0 : startIdx + 1} - {endIdx} dari {totalFilteredCount} transaksi
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400 bg-white rounded-2xl border border-slate-200/80 text-sm">
            Memuat data profit penjualan…
          </div>
        ) : totalFilteredCount === 0 ? (
          <div className="py-16 text-center text-slate-400 bg-white rounded-2xl border border-slate-200/80 text-sm">
            Tidak ada transaksi pada periode ini.
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedTransactions.map((tx, txIdx) => {
              const txId = tx.id || tx.transaction_no || txIdx;
              const isExpanded = expandedTxIds[txId] ?? (txIdx === 0 && safeCurrentPage === 1);
              const items = tx.items || [];
              const isShowAll = showAllItemsMap[txId];
              const visibleItems = isShowAll ? items : items.slice(0, 4);
              const remainingCount = items.length - 4;

              return (
                <div
                  key={txId}
                  className={`bg-white rounded-2xl border shadow-xs overflow-hidden transition-all ${
                    tx.is_return || tx.isReturn
                      ? "border-rose-200 bg-rose-50/10"
                      : "border-slate-200/80"
                  }`}
                >
                  {/* Accordion Header */}
                  <div
                    onClick={() => toggleExpand(txId)}
                    className={`p-4 sm:p-5 flex items-center justify-between gap-4 cursor-pointer select-none transition-colors ${
                      tx.is_return || tx.isReturn
                        ? "hover:bg-rose-50/40 bg-rose-50/20"
                        : "hover:bg-slate-50/60"
                    }`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        tx.is_return || tx.isReturn
                          ? "bg-rose-100 text-rose-600"
                          : "bg-slate-100 text-slate-500"
                      }`}>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 flex-1 min-w-0 items-center">
                        <div className="sm:col-span-5 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                              {tx.is_return || tx.isReturn ? "NO. RETUR" : "NO. TRANSAKSI"}
                            </p>
                            {(tx.is_return || tx.isReturn) && (
                              <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-rose-100 text-rose-700">
                                Retur Penjualan
                              </span>
                            )}
                          </div>
                          <div className="flex items-baseline gap-1 min-w-0 flex-wrap">
                            <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                              {tx.transaction_no}
                            </p>
                            {(tx.reference_number || tx.referenceNumber) && (
                              <span className="text-[11px] font-normal text-slate-500 truncate flex-shrink-0">
                                (Ref: {tx.reference_number || tx.referenceNumber})
                              </span>
                            )}
                            {Number(tx.additional_costs || 0) > 0 && (
                              <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200/60 shrink-0">
                                +Biaya Rp {fmt(tx.additional_costs)}
                              </span>
                            )}
                            {Number(tx.discount_total || 0) > 0 && (
                              <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-rose-50 text-rose-700 border border-rose-200/60 shrink-0">
                                -Diskon Rp {fmt(tx.discount_total)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="sm:col-span-3 min-w-0">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            TANGGAL
                          </p>
                          <p className="text-xs sm:text-sm font-medium text-slate-700 truncate">
                            {formatDateSimple(tx.date)}
                          </p>
                        </div>

                        <div className="sm:col-span-4 min-w-0">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            PELANGGAN
                          </p>
                          <p className="text-xs sm:text-sm font-medium text-slate-800 truncate">
                            {tx.customer}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0 text-right">
                      <div className="min-w-[90px] sm:min-w-[110px]">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider truncate">
                          {tx.is_return || tx.isReturn ? "PENGEMBALIAN DANA" : "SUBTOTAL"}
                        </p>
                        <p className={`text-xs sm:text-sm font-bold truncate ${
                          Number(tx.subtotal || 0) < 0 ? "text-rose-600" : "text-slate-900"
                        }`}>
                          {Number(tx.subtotal || 0) < 0 ? `-Rp ${fmt(Math.abs(tx.subtotal))}` : `Rp ${fmt(tx.subtotal)}`}
                        </p>
                      </div>

                      <div className="min-w-[70px] sm:min-w-[90px]">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                          PROFIT
                        </p>
                        <p
                          className={`text-xs sm:text-sm font-bold truncate ${
                            Number(tx.profit || 0) < 0
                              ? "text-rose-600"
                              : "text-[#0f766e]"
                          }`}
                        >
                          {Number(tx.profit || 0) < 0 ? `-Rp ${fmt(Math.abs(tx.profit))}` : `Rp ${fmt(tx.profit)}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Accordion Content (Items Table) */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/40 p-4 sm:p-5 animate-in fade-in duration-150">
                      <div className="bg-white rounded-xl border border-slate-200/70 overflow-hidden shadow-xs">
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs sm:text-sm">
                            <thead>
                              <tr className="border-b border-slate-100 bg-slate-50/70 text-slate-400 text-[11px] font-bold text-left uppercase tracking-wider">
                                <th className="py-2.5 px-4 font-bold">NAMA PRODUK</th>
                                <th className="py-2.5 px-3 text-center font-bold">QTY</th>
                                <th className="py-2.5 px-3 text-right font-bold">HARGA JUAL</th>
                                <th className="py-2.5 px-3 text-right font-bold">MODAL</th>
                                <th className="py-2.5 px-3 text-right font-bold">TOTAL PENJUALAN</th>
                                <th className="py-2.5 px-4 text-right font-bold">PROFIT</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {visibleItems.map((itm, itmIdx) => {
                                const isLoss = Number(itm.profit || 0) < 0;
                                const isTotalNeg = Number(itm.total_price || 0) < 0;
                                const isAddCost = (itm.product_name || "").includes("Biaya Tambahan");
                                const isDisc = (itm.product_name || "").includes("Diskon");

                                return (
                                  <tr key={itmIdx} className={`transition-colors ${
                                    isAddCost ? "bg-amber-50/20 hover:bg-amber-50/40" : isDisc ? "bg-rose-50/20 hover:bg-rose-50/40" : "hover:bg-slate-50/60"
                                  }`}>
                                    <td className="py-3 px-4 font-semibold text-slate-800">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        {isAddCost && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 shrink-0">
                                            Biaya Tambahan
                                          </span>
                                        )}
                                        {isDisc && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 shrink-0">
                                            Diskon
                                          </span>
                                        )}
                                        <span>{itm.product_name}</span>
                                      </div>
                                      {Number(itm.discount_amount || 0) > 0 && (
                                        <div className="mt-1 text-[10px] font-medium text-rose-600">
                                          Diskon Item -Rp {fmt(itm.discount_amount)}
                                        </div>
                                      )}
                                      {Number(itm.additional_cost || 0) > 0 && (
                                        <div className="mt-0.5 text-[10px] font-medium text-blue-600">
                                          Biaya Tambahan Item +Rp {fmt(itm.additional_cost)}
                                        </div>
                                      )}
                                    </td>
                                    <td className="py-3 px-3 text-center font-medium text-slate-700">
                                      {itm.quantity}
                                    </td>
                                    <td className="py-3 px-3 text-right font-medium text-slate-700">
                                      {Number(itm.unit_price || 0) < 0 ? `-Rp ${fmt(Math.abs(itm.unit_price))}` : `Rp ${fmt(itm.unit_price)}`}
                                    </td>
                                    <td className="py-3 px-3 text-right font-medium text-slate-700">
                                      Rp {fmt(itm.purchase_price)}
                                    </td>
                                    <td className={`py-3 px-3 text-right font-semibold ${
                                      isTotalNeg ? "text-rose-600" : "text-slate-900"
                                    }`}>
                                      {isTotalNeg ? `-Rp ${fmt(Math.abs(itm.total_price))}` : `Rp ${fmt(itm.total_price)}`}
                                    </td>
                                    <td
                                      className={`py-3 px-4 text-right font-bold ${
                                        isLoss ? "text-rose-600" : "text-[#0f766e]"
                                      }`}
                                    >
                                      {isLoss ? "-Rp " : "Rp "}{fmt(Math.abs(itm.profit))}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Toggle Show Remaining Items */}
                        {items.length > 4 && (
                          <div className="p-2.5 border-t border-slate-100 bg-slate-50/50 text-center">
                            <button
                              onClick={() => toggleShowAllItems(txId)}
                              className="text-xs font-semibold text-[#0f766e] hover:text-[#0d665f] transition-colors inline-flex items-center gap-1"
                            >
                              <span>
                                {isShowAll
                                  ? "Tampilkan lebih sedikit"
                                  : `Tampilkan ${remainingCount} item lainnya`}
                              </span>
                              {isShowAll ? (
                                <ChevronUp className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronDown className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination Controls */}
            {totalFilteredCount > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-slate-200/80 shadow-xs text-xs sm:text-sm text-slate-600">
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 font-medium">Baris per halaman:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2.5 py-1.5 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-xs sm:text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0f766e] cursor-pointer"
                  >
                    {[10, 20, 50, 100].map((limit) => (
                      <option key={limit} value={limit}>
                        {limit}
                      </option>
                    ))}
                  </select>
                  <span className="text-slate-500">
                    Menampilkan <b className="text-slate-800">{totalFilteredCount === 0 ? 0 : startIdx + 1}</b> - <b className="text-slate-800">{endIdx}</b> dari <b className="text-slate-800">{totalFilteredCount}</b> Transaksi
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={safeCurrentPage <= 1}
                    className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 inline-flex items-center justify-center transition shadow-2xs"
                    title="Halaman pertama"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safeCurrentPage <= 1}
                    className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 inline-flex items-center justify-center transition shadow-2xs"
                    title="Halaman sebelumnya"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-3 py-1 rounded-lg bg-[#0f766e] text-white text-xs sm:text-sm font-bold min-w-8 text-center shadow-xs">
                    {safeCurrentPage}
                  </span>
                  <span className="text-slate-400 text-xs font-medium">/ {maxPage}</span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(maxPage, p + 1))}
                    disabled={safeCurrentPage >= maxPage}
                    className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 inline-flex items-center justify-center transition shadow-2xs"
                    title="Halaman berikutnya"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(maxPage)}
                    disabled={safeCurrentPage >= maxPage}
                    className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 inline-flex items-center justify-center transition shadow-2xs"
                    title="Halaman terakhir"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === BOTTOM FOOTER TOTAL KESELURUHAN === */}
      <div className="sticky bottom-4 z-10 bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/90 shadow-lg flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-800">
            TOTAL KESELURUHAN
          </p>
        </div>

        <div className="flex items-center gap-6 sm:gap-10">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Total Penjualan
            </p>
            <p className="text-sm sm:text-base font-extrabold text-slate-900">
              Rp {fmt(totalSales)}
            </p>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Total Profit
            </p>
            <p className="text-sm sm:text-base font-extrabold text-[#0f766e]">
              Rp {fmt(totalProfit)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
